import type { ActivityFormData, CommentFormData, Deal, DealFormData } from "../../types/deal";
import { apiClient } from "../core/apiClient";

interface getDealResult {
  deals: Deal[];
  pagination: {
    page: number,
    limit: number,
    total: number,
    pages: number
  }
}

interface getStatusCountResult {
  status_counts: [
    {
      status_id: number,
      status_name: string,
      deal_count: number,
      process: {
        id: number,
        stage: number
      }
    }
  ],
  total_statuses: number
}

interface postDealResult {
  user: {
    employeeNo: string,
    name: string,
    userType: string,
    doorRight: string
  }
}

interface results<T = any> {
  data: T
}


export class DealService {

  async getDeals(params?: { page?: number }): Promise<getDealResult> {
    const queryString = params ? new URLSearchParams(params as any).toString() : '';
    const endpoint = `/deals${queryString ? `?${queryString}` : ''}`;
    const response = await apiClient.get<getDealResult>(endpoint, { timeout: 50000 });
    return response.data;
  }

  async getDealsbyId(id: number): Promise<Deal> {
    const endpoint = `/deals/${id}`;
    const response = await apiClient.get<Deal>(endpoint);
    return response.data;
  }

  async getProcesses(): Promise<results> {
    const response = await apiClient.get<results>(`/deals/processes`);
    return response;
  }

  async getActivities(id: number): Promise<results> {
    const response = await apiClient.get<results>(`/activity-deals/deal/${id}`);
    return response;
  }

  async getUsers(): Promise<results> {
    const response = await apiClient.get<results>(`/users`);
    return response;
  }

  async getComments(id: number): Promise<results> {
    const response = await apiClient.get<results>(`/comments-deals/deal/${id}`);
    return response;
  }

  async getStatusCount(): Promise<getStatusCountResult> {
    const response = await apiClient.get<getStatusCountResult>(`/deals/status/count`);
    return response.data;
  }


  async createDeal(data: DealFormData): Promise<{ success: Boolean, error?: string }> {
    try {
      const response = await apiClient.post<postDealResult>('/deals', data);
      if (response.state) {
        return {
          success: response.state
        };
      }
      return {
        success: false
      };
    } catch (error) {
      console.error('Error al crear deal:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : String(error)
      };
    }
  }

  async createComment(data: CommentFormData): Promise<{ success: Boolean, error?: string, message?: string }> {
    try {
      const response = await apiClient.post<postDealResult>('/comments-deals', data);
      if (response.state) {
        return {
          success: response.state,
          message: response.message
        };
      }
      return {
        success: false
      };
    } catch (error) {
      console.error('Error al crear deal:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : String(error)
      };
    }
  }

  async createActivity(data: ActivityFormData): Promise<{ success: Boolean, error?: string, message?: string }> {
    try {
      const response = await apiClient.post<postDealResult>('/activity-deals', data);
      if (response.state) {
        return {
          success: response.state,
          message: response.message
        };
      }
      return {
        success: false
      };
    } catch (error) {
      console.error('Error al crear deal:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : String(error)
      };
    }
  }

  async updateStatus(id: number, deal_process_id: number, deal_status_id: number): Promise<{ success: Boolean, error?: string, message?: string }> {
    try {
      const process_id = Number(deal_process_id);
      const status_id = Number(deal_status_id);
      const response = await apiClient.patch<postDealResult>(`/deals/${id}/status`, {deal_process_id: process_id, deal_status_id: status_id});
      if (response.state) {
        return {
          success: response.state,
          message: response.message
        };
      }
      return {
        success: false
      };
    } catch (error) {
      console.error('Error al crear deal:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : String(error)
      };
    }
  }

  async updateDeal(formData: Deal): Promise<{ success: Boolean, error?: string, message?: string }> {
    try {
      const response = await apiClient.put<postDealResult>(`/deals/${formData.id}`, formData);
      if (response.state) {
        return {
          success: response.state,
          message: response.message
        };
      }
      return {
        success: false
      };
    } catch (error) {
      console.error('Error al crear deal:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : String(error)
      };
    }
  }

  async createAsesor(deal_id: number, user_id: number): Promise<{ success: Boolean, error?: string, message?: string }> {
    try {
      const response = await apiClient.post<postDealResult>(`/owner-deals`, {deal_id, user_id});
      if (response.state) {
        return {
          success: response.state,
          message: response.message
        };
      }
      return {
        success: false
      };
    } catch (error) {
      console.error('Error al crear asesor:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : String(error)
      };
    }
  }

  async updateAsesor(deal_id: number, user_id: number): Promise<{ success: Boolean, error?: string, message?: string }> {
    try {
      const response = await apiClient.put<postDealResult>(`/owner-deals/${deal_id}`, {deal_id, user_id});
      if (response.state) {
        return {
          success: response.state,
          message: response.message
        };
      }
      return {
        success: false
      };
    } catch (error) {
      console.error('Error al editar asesor:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : String(error)
      };
    }
  }
}

export const dealService = new DealService();